import { Box, HBox, VBox } from "@ui/box/Box"
import Button from "@ui/button/Button"
import { Skeleton } from "@ui/skeleton/Skeleton"
import { ProgressBar } from "@ui/spinner/ProgressBar"
import { Typo } from "@ui/text/Typo"
import { useCallback, useEffect, useState } from "react"
import { GD } from "src/GD"
import { PRIVATE_MOBILE_BREAKPOINT as MOBILE, PRIVATE_NARROW_BREAKPOINT as NARROW } from "../theme"
import { DS, StatGroup } from "../../ds/Kit"
import { StatusBadge } from "../../components/StatusBadge"
import { getFriendlyErrorMessage } from "src/utils/errors"
import { formatPrivateMoney } from "../money"
import { getLastDaysRange, getLastMonthsRange, getThisMonthRange, getThisWeekRange, getTodayRange, getYesterdayRange } from "./transactionDateRange"
import { nativeSelectStyle } from "../nativeSelectStyle"
import { Callout, EmptyState, PageIntro, PrivateRequestError, PrivateSurface } from "../ui"
import { TransactionDetails } from "../../components/TransactionDetails"
import { S_UNIVERSAL_DIALOG } from "../../components/universalDialog/signals"
import { getTransactionDisplayLabel } from "src/utils/transactionPresentation"
import { TransactionFilterPresets } from "../../components/TransactionFilterPresets"
import styled from "styled-components"

const FilterGrid = styled(Box)<{ $expanded: boolean }>`
	${MOBILE} {
		> :not([data-filter-search]) { display: ${props => props.$expanded ? "block" : "none"}; }
		> [data-filter-search] { grid-column: 1 / -1; }
		> :not([data-filter-search]) { animation: v2-enter 240ms var(--v2-ease) both; }
	}
`

const PAGE_SIZE = 20
const PERCENT = 100
const EQUAL_SPLIT_PERCENT = 50
const LAST_FOUR_DIGITS = 4
const SEARCH_DEBOUNCE_MS = 300
const LAST_WEEK_DAYS = 7
const LAST_MONTH_DAYS = 30
const LAST_QUARTER_MONTHS = 3
const DEFAULT_PERIOD = "3months"

type TTransactionPeriod = "all" | "today" | "yesterday" | "week" | "month" | "7days" | "30days" | "3months" | "custom"

export const PrivateTransactions = ({ cardUid, onClearCard, onTransactions }: { cardUid?: string; onClearCard?: () => void; onTransactions?: (cardUid: string) => void } = {}) => {
	const [data, setData] = useState<ICompanyTransactionsResponse | null>(null)
	const [page, setPage] = useState(1)
	const [loading, setLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [reloadKey, setReloadKey] = useState(0)
	const [direction, setDirection] = useState<"" | "income" | "expense">("")
	const [txType, setTxType] = useState<TCompanyTransactionType | "">("")
	const [status, setStatus] = useState("")
	const [currency, setCurrency] = useState("")
	const [minAmount, setMinAmount] = useState("")
	const [maxAmount, setMaxAmount] = useState("")
	const [period, setPeriod] = useState<TTransactionPeriod>(DEFAULT_PERIOD)
	const [customFrom, setCustomFrom] = useState("")
	const [customTo, setCustomTo] = useState("")
	const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false)
	const [search, setSearch] = useState("")
	const [debouncedSearch, setDebouncedSearch] = useState("")
	const [selectedCardUid, setSelectedCardUid] = useState("")
	const [cards, setCards] = useState<ICardSaved[]>([])
	const [filterOptions, setFilterOptions] = useState<ICompanyTransactionFiltersResponse | null>(null)
	const [appliedPresetFilter, setAppliedPresetFilter] = useState<ICompanyTransactionFilter | null>(null)
	const effectiveCardUid = cardUid || selectedCardUid
	const getCurrentDateRange = useCallback(() => period === "today"
		? getTodayRange()
		: period === "yesterday"
			? getYesterdayRange()
			: period === "week"
				? getThisWeekRange()
				: period === "month"
					? getThisMonthRange()
					: period === "7days"
						? getLastDaysRange(LAST_WEEK_DAYS)
						: period === "30days"
							? getLastDaysRange(LAST_MONTH_DAYS)
							: period === "3months"
								? getLastMonthsRange(LAST_QUARTER_MONTHS)
							: period === "custom" && (customFrom || customTo)
								// An open-ended period runs up to today.
								? { ...(customFrom ? { from: customFrom } : {}), to: customTo || getTodayRange().to }
								: null, [customFrom, customTo, period])

	useEffect(() => {
		let alive = true
		GD.REQ_COMPANY_TRANSACTION_FILTERS.request()
			.then(response => {
				if (alive && response.data) setFilterOptions(response.data)
			})
			.catch(() => undefined)
		return () => { alive = false }
	}, [])

	useEffect(() => {
		if (search.trim() === debouncedSearch) return
		const timer = window.setTimeout(() => {
			setDebouncedSearch(search.trim())
			setPage(1)
		}, SEARCH_DEBOUNCE_MS)
		return () => window.clearTimeout(timer)
	}, [search, debouncedSearch])

	useEffect(() => {
		let alive = true
		Promise.resolve()
			.then(() => GD.REQ_CARDS.request())
			.then(response => {
				if (alive && response.data) setCards(response.data)
			})
			.catch(() => undefined)
		return () => { alive = false }
	}, [])

	useEffect(() => {
		let alive = true
		;(async () => {
			setLoading(true)
			setError(null)
			try {
				const dateRange = getCurrentDateRange()
				const filter: ICompanyTransactionFilter = appliedPresetFilter ? {
					...appliedPresetFilter,
					page: page - 1,
					limit: PAGE_SIZE,
				} : {
					page: page - 1,
					limit: PAGE_SIZE,
					...(direction ? { direction } : {}),
					...(txType ? { tx_type: txType } : {}),
					...(status ? { status } : {}),
					...(currency ? { currency } : {}),
					...(Number(minAmount) > 0 ? { min_amount: Math.round(Number(minAmount) * PERCENT) } : {}),
					...(Number(maxAmount) > 0 ? { max_amount: Math.round(Number(maxAmount) * PERCENT) } : {}),
					...(debouncedSearch ? { search: debouncedSearch } : {}),
					...(dateRange ?? {})
				}
				const res = effectiveCardUid && !appliedPresetFilter
					? await GD.REQ_COMPANY_TRANSACTIONS_CARD.request({ ...filter, card_uid: effectiveCardUid })
					: await GD.REQ_COMPANY_TRANSACTIONS_ALL.request(filter)
				if (!alive) return
				if (res.error) {
					setError(getFriendlyErrorMessage(res.error, "Could not load transactions"))
					return
				}
				setData(res.data ?? null)
			} catch (cause) {
				if (alive) setError(getFriendlyErrorMessage(cause, "Could not load transactions"))
			} finally {
				if (alive) setLoading(false)
			}
		})()
		return () => {
			alive = false
		}
	}, [appliedPresetFilter, currency, customFrom, customTo, debouncedSearch, direction, effectiveCardUid, getCurrentDateRange, maxAmount, minAmount, page, period, reloadKey, status, txType])

	const txs = data?.transactions ?? []
	const selectedCard = cardUid ? cards.find(card => card.uid === cardUid) : undefined
	const selectedCardLabel = selectedCard
		? [selectedCard.name, selectedCard.mask ? `•••• ${selectedCard.mask.slice(-LAST_FOUR_DIGITS)}` : null].filter(Boolean).join(" · ")
		: cardUid
	const pages = data?.pagination?.pages ?? 1
	const visibleIncome = txs.filter(tx => tx.direction === "income").reduce((sum, tx) => sum + Number(tx.amount || 0), 0)
	const visibleExpense = txs.filter(tx => tx.direction === "expense").reduce((sum, tx) => sum + Number(tx.amount || 0), 0)
	const visibleTotal = visibleIncome + visibleExpense
	const activeFilterCount = [search.trim(), txType, direction, status, selectedCardUid, period !== DEFAULT_PERIOD, currency, minAmount, maxAmount]
		.filter(Boolean).length
	const resetFilters = () => {
		setAppliedPresetFilter(null)
		setSearch("")
		setDebouncedSearch("")
		setTxType("")
		setDirection("")
		setStatus("")
		setSelectedCardUid("")
		setPeriod(DEFAULT_PERIOD)
		setCustomFrom("")
		setCustomTo("")
		setCurrency("")
		setMinAmount("")
		setMaxAmount("")
		setAdvancedFiltersOpen(false)
		setPage(1)
	}
	const currentDateRange = getCurrentDateRange()
	const currentPresetFilter: ICompanyTransactionFilter = {
		...(direction ? { direction } : {}),
		...(txType ? { tx_type: txType } : {}),
		...(status ? { status } : {}),
		...(currency ? { currency } : {}),
		...(Number(minAmount) > 0 ? { min_amount: Math.round(Number(minAmount) * PERCENT) } : {}),
		...(Number(maxAmount) > 0 ? { max_amount: Math.round(Number(maxAmount) * PERCENT) } : {}),
		...(search.trim() ? { search: search.trim() } : {}),
		...(effectiveCardUid ? { card_uid: effectiveCardUid } : {}),
		...(currentDateRange ?? {}),
	}
	const applyPreset = (preset: ITransactionFilterPreset) => {
		const presetFilter = preset.filter
		setAppliedPresetFilter(presetFilter)
		setSearch(presetFilter.search || "")
		setDebouncedSearch(presetFilter.search || "")
		setTxType(presetFilter.tx_type && presetFilter.tx_type !== "any" ? presetFilter.tx_type : "")
		setDirection(presetFilter.direction || "")
		setStatus(presetFilter.status || "")
		setCurrency(presetFilter.currency || "")
		setMinAmount(presetFilter.min_amount === undefined ? "" : String(presetFilter.min_amount / PERCENT))
		setMaxAmount(presetFilter.max_amount === undefined ? "" : String(presetFilter.max_amount / PERCENT))
		setSelectedCardUid(presetFilter.card_uid || "")
		setCustomFrom(presetFilter.from || "")
		setCustomTo(presetFilter.to || "")
		const periodMap: Partial<Record<TTransactionDatePeriod, TTransactionPeriod>> = {
			all_time: "all", today: "today", yesterday: "yesterday", last_7_days: "7days",
			last_30_days: "30days", this_week: "week", this_month: "month", custom: "custom",
		}
		setPeriod(periodMap[preset.date_period] || "custom")
		setPage(1)
	}
	const openTransactionDetails = (transaction: ICompanyTrxTransaction) => {
		S_UNIVERSAL_DIALOG.invoke({
			label: "Transaction Details",
			closeDialog: () => S_UNIVERSAL_DIALOG.invoke(null),
			sx: { maxWidth: "600px", width: "100%" },
			content: <TransactionDetails transaction={transaction} onTransactions={onTransactions} privateCardActionsOnly />
		})
	}

	const fieldStyle = { width: "100%", minWidth: 0, height: "44px", boxSizing: "border-box" as const, padding: "0 12px", fontSize: "14px", border: `1px solid ${DS.line}`, borderRadius: "12px", backgroundColor: "#fff", color: DS.ink, fontFamily: "inherit" }
	const selectStyle = { ...nativeSelectStyle, ...fieldStyle, paddingRight: "36px", backgroundPosition: "right 10px center" }
	const incomePercent = visibleTotal ? (visibleIncome / visibleTotal) * PERCENT : EQUAL_SPLIT_PERCENT
	// Keep the previous rows visible, dimmed, while a filter change loads; search waits for its debounce.
	const refreshing = !!data && (loading || search.trim() !== debouncedSearch)
	const recordsLabel = refreshing ? "Updating…" : loading ? "Loading…" : error ? "Unavailable" : data?.pagination?.total ? `${data.pagination.total} ${data.pagination.total === 1 ? "record" : "records"}` : "No records"

	return (
		<VBox gap="16px">
			<PageIntro title="Transactions" description="See money in, money out and transaction dates." />
			{cardUid && (
				<Callout
					tone="info"
					title="Showing transactions for"
					action={onClearCard && <Button title="Show all transactions" variant="secondary" onClick={onClearCard} />}
				>
					<span style={{ fontWeight: 650 }}>{selectedCardLabel}</span>
				</Callout>
			)}

			{!loading && !error && txs.length > 0 && (
				<StatGroup
					items={[
						{ label: "Funds in", value: `+${formatPrivateMoney(visibleIncome, txs[0]?.currency)}`, hint: "This page" },
						{ label: "Funds out", value: `−${formatPrivateMoney(visibleExpense, txs[0]?.currency)}`, hint: "This page" },
						{ label: "Net movement", value: `${visibleIncome - visibleExpense >= 0 ? "+" : "−"}${formatPrivateMoney(Math.abs(visibleIncome - visibleExpense), txs[0]?.currency)}`, hint: "This page" },
						{
							label: "Visible flow",
							value: `${Math.round(incomePercent)}% in`,
							hint: (
								<VBox gap="6px">
									<HBox sx={{ height: "6px", overflow: "hidden", borderRadius: "999px", backgroundColor: DS.line }}><Box sx={{ width: `${incomePercent}%`, minWidth: visibleIncome ? "4px" : 0, height: "100%", backgroundColor: "#10B981" }} /><Box sx={{ flex: 1, minWidth: visibleExpense ? "4px" : 0, height: "100%", backgroundColor: "#D68B39" }} /></HBox>
									<HBox gap="12px" sx={{ alignItems: "center" }}><FlowKey color="#10B981" label="Incoming" /><FlowKey color="#D68B39" label="Outgoing" /></HBox>
								</VBox>
							)
						}
					]}
				/>
			)}

			{/* Toolbar */}
			<PrivateSurface sx={{ padding: "14px 16px" }}>
				<HBox sx={{ justifyContent: "space-between", alignItems: "center", gap: "12px", marginBottom: "12px", flexWrap: "wrap" }}>
					<HBox gap="8px" sx={{ alignItems: "center" }}>
						<Box aria-hidden sx={{ display: "flex", color: DS.accent }}><IconFilter /></Box>
						<Typo sx={{ color: DS.ink, fontSize: "15px", fontWeight: 650 }}>Filters</Typo>
						{activeFilterCount > 0 && <Box sx={{ minWidth: "22px", height: "22px", padding: "0 7px", borderRadius: "999px", display: "flex", alignItems: "center", justifyContent: "center", color: DS.accentInk, backgroundColor: DS.accentSoft, fontSize: "12px", fontWeight: 700 }}>{activeFilterCount}</Box>}
					</HBox>
					<HBox gap="8px" sx={{ alignItems: "center" }}>
						{activeFilterCount > 0 && <Button title="Clear filters" variant="empty" onClick={resetFilters} sx={{ minHeight: "40px", padding: "0 10px", color: DS.muted, fontSize: "13px", boxShadow: "none" }} />}
						<Button title={advancedFiltersOpen ? "Hide filters" : "More filters"} variant="secondary" onClick={() => setAdvancedFiltersOpen(open => !open)} sx={{ minHeight: "40px", padding: "0 14px", borderRadius: "10px", color: advancedFiltersOpen ? DS.accentInk : DS.ink, borderColor: advancedFiltersOpen ? DS.accent : DS.line, backgroundColor: advancedFiltersOpen ? DS.accentSoft : DS.surface, fontSize: "13px" }} />
					</HBox>
				</HBox>
				{!cardUid && <Box sx={{ marginBottom: "12px", [MOBILE]: { display: advancedFiltersOpen ? "block" : "none" } }}>
					<TransactionFilterPresets
						datePeriodOptions={filterOptions?.date_period}
						currentFilter={currentPresetFilter}
						active={appliedPresetFilter !== null}
						onApply={applyPreset}
					/>
				</Box>}
				<FilterGrid $expanded={advancedFiltersOpen} sx={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "10px", "@media (max-width: 900px)": { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }, [NARROW]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
				<Box data-filter-search sx={{ gridColumn: "span 2", [NARROW]: { gridColumn: "auto" } }}><input aria-label="Search transactions" value={search} onChange={event => { setAppliedPresetFilter(null); setSearch(event.target.value) }} placeholder="Search transactions" style={fieldStyle} /></Box>
				<select aria-label="Transaction type" value={txType} onChange={event => { setAppliedPresetFilter(null); setTxType(event.target.value as TCompanyTransactionType | ""); setPage(1) }} style={selectStyle}>
					<option value="">All types</option>{(filterOptions?.tx_type ?? [{ value: "spent", label: "Spent" }, { value: "returned", label: "Returned" }, { value: "commission", label: "Commission" }, { value: "refund", label: "Refund" }, { value: "deposit", label: "Deposit" }, { value: "other", label: "Other" }]).filter(option => option.value !== "any").map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
				</select>
				<select aria-label="Date range" value={period} onChange={event => { setAppliedPresetFilter(null); setPeriod(event.target.value as TTransactionPeriod); setPage(1) }} style={selectStyle}>
					<option value="all">All time</option><option value="today">Today</option><option value="yesterday">Yesterday</option><option value="7days">Last 7 days</option><option value="30days">Last 30 days</option><option value="3months">Last 3 months</option><option value="week">This week</option><option value="month">This month</option><option value="custom">Custom dates</option>
				</select>
				<Box sx={{ gridColumn: cardUid ? "span 2" : "auto", [NARROW]: { gridColumn: "auto" } }}><select aria-label="Direction" value={direction} onChange={event => { setAppliedPresetFilter(null); setDirection(event.target.value as "" | "income" | "expense"); setPage(1) }} style={selectStyle}>
					<option value="">Any direction</option>{(filterOptions?.direction ?? [{ value: "income", label: "Funds in" }, { value: "expense", label: "Payments" }]).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
				</select></Box>
				<Box sx={{ gridColumn: cardUid ? "span 2" : "auto", [NARROW]: { gridColumn: "auto" } }}><select aria-label="Transaction status" value={status} onChange={event => { setAppliedPresetFilter(null); setStatus(event.target.value); setPage(1) }} style={selectStyle}>
					<option value="">All statuses</option>{(filterOptions?.status ?? [{ value: "completed", label: "Completed" }, { value: "pending", label: "Pending" }, { value: "error", label: "Error" }]).map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
				</select></Box>
				{!cardUid && <Box sx={{ gridColumn: "span 2", [NARROW]: { gridColumn: "auto" } }}><select aria-label="Card" value={selectedCardUid} onChange={event => { setAppliedPresetFilter(null); setSelectedCardUid(event.target.value); setPage(1) }} style={selectStyle}>
					<option value="">All cards</option>
					{cards.map(card => <option key={card.uid} value={card.uid}>{card.name || (card.mask ? `•••• ${card.mask.slice(-LAST_FOUR_DIGITS)}` : card.uid)}</option>)}
				</select></Box>}
				{period === "custom" && <><input aria-label="Date from" type="date" value={customFrom} onChange={event => { setAppliedPresetFilter(null); setCustomFrom(event.target.value); setPage(1) }} style={fieldStyle} /><input aria-label="Date to" type="date" value={customTo} onChange={event => { setAppliedPresetFilter(null); setCustomTo(event.target.value); setPage(1) }} style={fieldStyle} /></>}
				</FilterGrid>
				{advancedFiltersOpen && <Box sx={{ marginTop: "12px", paddingTop: "12px", borderTop: `1px solid ${DS.line}` }}>
					<Typo sx={{ marginBottom: "8px", color: DS.muted, fontSize: "13px", fontWeight: 600 }}>Amount and currency</Typo>
					<Box sx={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: "10px", [MOBILE]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" }, [NARROW]: { gridTemplateColumns: "minmax(0, 1fr)" } }}>
						<select aria-label="Currency" value={currency} onChange={event => { setAppliedPresetFilter(null); setCurrency(event.target.value); setPage(1) }} style={selectStyle}>
							<option value="">All currencies</option>{Array.from(new Set(cards.map(card => card.currency).filter((value): value is string => Boolean(value)))).map(value => <option key={value} value={value}>{value}</option>)}
						</select>
						<input aria-label="Minimum amount" type="number" min="0" step="0.01" value={minAmount} onChange={event => { setAppliedPresetFilter(null); setMinAmount(event.target.value); setPage(1) }} placeholder="Amount from" style={fieldStyle} />
						<input aria-label="Maximum amount" type="number" min="0" step="0.01" value={maxAmount} onChange={event => { setAppliedPresetFilter(null); setMaxAmount(event.target.value); setPage(1) }} placeholder="Amount to" style={fieldStyle} />
					</Box>
				</Box>}
			</PrivateSurface>

			{/* Ledger */}
			<PrivateSurface sx={{ overflow: "hidden" }}>
				<HBox sx={{ justifyContent: "space-between", alignItems: "center", gap: "12px", padding: "16px 20px 12px", [MOBILE]: { padding: "14px 16px 10px" } }}>
					<Typo sx={{ fontWeight: 650, fontSize: "16px", color: DS.ink }}>Transaction history</Typo>
					<Typo sx={{ color: DS.muted, fontSize: "13px" }}>{recordsLabel}</Typo>
				</HBox>
				<ProgressBar busy={refreshing} color={DS.accent} label="Updating transactions" />

				{loading && !data ? (
					// Row-shaped placeholders keep the ledger's height steady while it loads.
					<VBox gap="10px" sx={{ padding: "8px 18px 18px", [MOBILE]: { padding: "8px 14px 14px" } }}>
						{[0, 1, 2, 3, 4].map(index => (
							<Skeleton key={index} mode="manual" busy shape="rect" width="100%" height="58px" sx={{ borderRadius: "14px" }} />
						))}
					</VBox>
				) : error ? (
					<Box sx={{ padding: "8px 18px 18px", [MOBILE]: { padding: "8px 14px 14px" } }}><PrivateRequestError message={error} onRetry={() => setReloadKey(key => key + 1)} /></Box>
				) : txs.length === 0 ? (
					<EmptyState icon={<IconActivity />} title={activeFilterCount ? "No matching transactions" : "No transactions"} description={activeFilterCount ? "Try changing or clearing your filters." : "Incoming and outgoing transactions will appear here."} action={activeFilterCount ? <Button title="Clear filters" variant="secondary" onClick={resetFilters} /> : undefined} />
				) : (
					<VBox sx={{ opacity: refreshing ? 0.5 : 1, transition: "opacity .15s", pointerEvents: refreshing ? "none" : undefined }}>
						<Box aria-hidden sx={{ display: "grid", gridTemplateColumns: LEDGER_COLUMNS, columnGap: "16px", padding: "10px 20px", backgroundColor: DS.soft, borderTop: `1px solid ${DS.line}`, [MOBILE]: { display: "none" } }}>
							{["Date", "Description", "Card", "Amount", "Status"].map((heading, index) => <Typo key={heading} sx={{ color: DS.muted, fontSize: "12px", fontWeight: 500, textAlign: heading === "Amount" ? "right" : "left", ...(index === 4 ? { paddingLeft: 0 } : {}) }}>{heading}</Typo>)}
						</Box>
						{txs.map(tx => {
							const income = tx.direction === "income"
							const displayLabel = getTransactionDisplayLabel(tx, undefined, true)
							const created = new Date(tx.created_at)
							return (
								<Box
									key={tx.id}
									element="button"
									type="button"
									aria-label={`Open transaction details: ${displayLabel || tx.id}`}
									onClick={() => openTransactionDetails(tx)}
									sx={{
										width: "100%",
										display: "grid",
										gridTemplateColumns: LEDGER_COLUMNS,
										columnGap: "16px",
										rowGap: "6px",
										alignItems: "center",
										padding: "12px 20px",
										border: "none",
										borderTop: `1px solid ${DS.line}`,
										borderRadius: 0,
										backgroundColor: "transparent",
										font: "inherit",
										textAlign: "left",
										cursor: "pointer",
										transition: "background-color .16s ease",
										"&:hover": { backgroundColor: DS.soft },
										"&:focus-visible": { outline: `2px solid ${DS.accent}`, outlineOffset: "-3px" },
										[MOBILE]: { gridTemplateColumns: "minmax(0, 1fr) auto", padding: "14px 16px" }
									}}>
									<VBox gap="2px" sx={{ [MOBILE]: { gridRow: 2, gridColumn: 1 } }}>
										<Typo sx={{ color: DS.ink, fontSize: "13px", fontWeight: 600, whiteSpace: "nowrap" }}>{created.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</Typo>
										<Typo sx={{ color: DS.muted, fontSize: "12px" }}>{created.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}</Typo>
									</VBox>
									<HBox gap="12px" sx={{ alignItems: "center", minWidth: 0, [MOBILE]: { gridRow: 1, gridColumn: 1 } }}>
										<Box aria-hidden sx={{ width: "38px", height: "38px", borderRadius: "12px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", backgroundColor: income ? "#ECFDF5" : "#FEF2F2", color: income ? "#047857" : "#B42318" }}>
											<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d={income ? "M17 7 7 17M7 17h8M7 17V9" : "M7 17 17 7M17 7H9M17 7v8"} /></svg>
										</Box>
										<VBox gap="2px" sx={{ minWidth: 0 }}>
											<Typo sx={{ color: DS.ink, fontWeight: 600, fontSize: "14px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", [NARROW]: { whiteSpace: "normal", overflowWrap: "anywhere" } }}>
												{displayLabel || (income ? "Top up" : "Payment")}
											</Typo>
											{(tx.card_name || tx.card_mask) && <Typo sx={{ display: "none", color: DS.muted, fontSize: "12px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", [MOBILE]: { display: "block" } }}>{[tx.card_name, tx.card_mask && `•••• ${tx.card_mask.slice(-LAST_FOUR_DIGITS)}`].filter(Boolean).join(" ")}</Typo>}
										</VBox>
									</HBox>
									<Typo sx={{ color: DS.muted, fontSize: "13px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", [MOBILE]: { display: "none" } }}>{tx.card_mask ? `•••• ${tx.card_mask.slice(-LAST_FOUR_DIGITS)}` : "—"}</Typo>
									<Typo sx={{ fontWeight: 650, fontSize: "14px", textAlign: "right", color: income ? "#047857" : DS.ink, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap", [MOBILE]: { gridRow: 1, gridColumn: 2 } }}>
										{income ? "+" : "−"}
										{formatPrivateMoney(Number(tx.amount || 0), tx.currency)}
									</Typo>
									<HBox sx={{ [MOBILE]: { gridRow: 2, gridColumn: 2, justifyContent: "flex-end" } }}><StatusBadge status={tx.status} /></HBox>
								</Box>
							)
						})}
					</VBox>
				)}

				{pages > 1 && (
					<HBox sx={{ justifyContent: "center", alignItems: "center", gap: "16px", padding: "14px", borderTop: `1px solid ${DS.line}`, [NARROW]: { gap: "8px", padding: "14px 10px" } }}>
						<Button variant="secondary" disabled={page <= 1} onClick={() => setPage(p => Math.max(1, p - 1))} sx={{ [NARROW]: { padding: "10px 12px", minWidth: 0 } }}>
							Previous
						</Button>
						<Typo sx={{ color: DS.muted, fontSize: "13px" }}>
							Page {page} of {pages}
						</Typo>
						<Button variant="secondary" disabled={page >= pages} onClick={() => setPage(p => Math.min(pages, p + 1))} sx={{ [NARROW]: { padding: "10px 12px", minWidth: 0 } }}>
							Next
						</Button>
					</HBox>
				)}
			</PrivateSurface>
		</VBox>
	)
}

const LEDGER_COLUMNS = "104px minmax(0, 2.2fr) minmax(0, .9fr) 130px 110px"

const IconFilter = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
const IconActivity = () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M4 13h3l2 5 4-12 2 7h5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>

const FlowKey = ({ color, label }: { color: string; label: string }) => <HBox gap="6px" sx={{ alignItems: "center" }}><Box aria-hidden sx={{ width: "8px", height: "8px", borderRadius: "50%", backgroundColor: color }} /><Typo sx={{ color: DS.muted, fontSize: "12px" }}>{label}</Typo></HBox>
